// TODO(dev:dashboard_v2, translations): Add missing translations
import React from "react"
import { Icon } from "~/react/ui/icon"
import { cn } from "~/lib/utils"
import { Separator } from "../ui/separator"
import { Button } from "../ui/button"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { campaigns_inbox_index_path, leads_path } from "@/lib/routes"

interface HeadlineMetricItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: string
  iconColor?: string
  label: string
  value: string
}

const HeadlineMetricItem = React.forwardRef<
  HTMLDivElement,
  HeadlineMetricItemProps
>(({ className, icon, iconColor, label, value, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("headline-metric-item", className)} {...props}>
      <div className="headline-metric-item__top">
        {icon && (
          <Icon
            icon={icon}
            aria-hidden="true"
            size="sm"
            style={
              iconColor
                ? { color: iconColor }
                : { color: "var(--colors-grey-600)" }
            }
          />
        )}
        <span className="headline-metric-item__label">{label}</span>
      </div>
      <div className="headline-metric-item__bottom">
        <span className="headline-metric-item__value">{value}</span>
        {children}
      </div>
    </div>
  )
})

HeadlineMetricItem.displayName = "HeadlineMetricItem"

interface HeadlineMetricsProps extends React.HTMLAttributes<HTMLDivElement> {
  leads_count?: number
  leads_without_email?: number
  verified_leads?: number
  unread_inbox_messages?: number
}

const HeadlineMetrics = React.forwardRef<HTMLDivElement, HeadlineMetricsProps>(
  (
    {
      className,
      leads_count,
      leads_without_email,
      verified_leads,
      unread_inbox_messages,
      ...props
    },
    ref,
  ) => {
    // Guard against undefined and zero totals to avoid NaN/Infinity
    const totalLeads = leads_count ?? 0
    const withoutEmail = leads_without_email ?? 0
    const verified = verified_leads ?? 0

    const percentageOfLeadsWithEmail =
      totalLeads > 0 ? (((totalLeads - withoutEmail) / totalLeads) * 100) : 0
    const percentageOfVerifiedLeads =
      totalLeads > 0 ? ((verified / totalLeads) * 100) : 0
    return (
      <div ref={ref} className={cn("headline-metrics", className)} {...props}>
        <HeadlineMetricItem
          icon="users"
          iconColor="var(--colors-secondary-600)"
          label={i18n.t("js.dashboard.headline_metrics.total_leads")}
          value={CurrencyHelper.numberWithDelimiter(totalLeads)}
        />
        <Separator orientation="vertical" />
        <HeadlineMetricItem
          icon="at"
          iconColor="var(--colors-success-600)"
          label={i18n.t("js.dashboard.headline_metrics.with_email_address")}
          value={`${percentageOfLeadsWithEmail.toFixed(2)}%`}
        >
          <Button size="xs" variant="default" asChild>
            <a href={leads_path({ email: "~" })}>
              {i18n.t("js.dashboard.headline_metrics.show_missing")}
            </a>
          </Button>
        </HeadlineMetricItem>
        <Separator orientation="vertical" />
        <HeadlineMetricItem
          icon="check-circle"
          iconColor="var(--colors-success-600)"
          label={i18n.t("js.dashboard.headline_metrics.verified")}
          value={`${percentageOfVerifiedLeads.toFixed(2)}%`}
        >
          <Button size="xs" variant="default" asChild>
            <a
              href={leads_path({
                verification_status: ["error", "unknown", "~"],
              })}
            >
              {i18n.t("js.dashboard.headline_metrics.show_unverified")}
            </a>
          </Button>
        </HeadlineMetricItem>
        <Separator orientation="vertical" />
        <HeadlineMetricItem
          icon="inbox"
          iconColor="var(--colors-cyan-500)"
          label={i18n.t("js.dashboard.headline_metrics.new_replies")}
          value={CurrencyHelper.numberWithDelimiter(unread_inbox_messages)}
        >
          <Button size="xs" variant="default" asChild>
            <a href={campaigns_inbox_index_path()}>
              {i18n.t("js.dashboard.headline_metrics.go_to_inbox")}
            </a>
          </Button>
        </HeadlineMetricItem>
      </div>
    )
  },
)

HeadlineMetrics.displayName = "HeadlineMetrics"

export { HeadlineMetrics }
